// The access screen: the tenant's brand before anyone signs in, from
// bootstrap -- INC-12a (A.4): its logo, or its square mark with its name, or
// its name in ink, and nothing of the product's --; without a tenant, a
// neutral state and no form (D-77).
//
// INC-07b-2b-1 (D-290): a session of the office the server ended arrives
// here with the reason and the route it was on. The screen says the session
// expired, with the catalog's text, and once the person is in again sends
// them back to that route. The field arrives with nothing and goes, as it
// always did, to its own day; INC-14a-1 (B-16): a field session the server
// ended arrives with the reason too, and the day as its route. INC-12c-1
// (A.1): an address of the portal asked for with no session arrives with the
// route alone, and goes back to it the same way, with nothing said.
//
// INC-12c-2 (D-291): "Olvidé mi contraseña", the same for the three
// experiences, leads to the screen where a person asks for a link.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router';
import { isApiError, isNetworkError, CODES } from '../api/errors';
import { brandOf } from '../app/brand';
import { proposeDeviceLabel } from '../app/deviceLabel';
import { logUnexpected } from '../app/unexpected';
import { useRuntime, useSession } from '../app/hooks';
import type { SignInReturn } from '../app/WebGate';
import { useLiveQuery } from '../app/useLiveQuery';
import { useTitleSection } from '../app/documentTitle';
import { pendingCount } from '../db/database';
import { BrandLockup } from '../ui/BrandLockup';

/** What the gate of the web left for this screen, if anything: where to go
 *  back to, and whether the session there ended. */
function signInReturn(state: unknown): SignInReturn | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = state as Partial<SignInReturn>;
  return typeof candidate.from === 'string'
    ? { from: candidate.from, expired: candidate.expired === true }
    : null;
}

export function AccessScreen() {
  const { t } = useTranslation();
  // INC-14a-3a-1: the tab names this screen before the organization, and no product.
  useTitleSection(t('access.documentTitle'), 'outside');
  const { session, db, engine } = useRuntime();
  const snapshot = useSession();
  const back = signInReturn(useLocation().state);
  const pending = useLiveQuery(() => pendingCount(db), 0, [db]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Proposed from the stored device or the platform until the operator edits it.
  // INC-15b-2 (M, D-409): the name last used in this browser, or the one built
  // from the browser and the system; nobody has to write one, and a field left
  // empty takes the one built.
  const [deviceLabelInput, setDeviceLabelInput] = useState<string | null>(null);
  const deviceLabel =
    deviceLabelInput ?? snapshot.deviceLabel ?? proposeDeviceLabel(navigator.userAgent, t);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (snapshot.status === 'authenticated') return <Navigate to={back?.from ?? '/'} replace />;
  if (snapshot.status === 'password_change_required') return <Navigate to="/password" replace />;
  if (snapshot.status === 'revoked' || snapshot.status === 'suspended') {
    return <Navigate to="/status" replace />;
  }

  const organization = snapshot.bootstrap?.organization ?? null;
  const brand = brandOf(snapshot);
  const unknownTenant = snapshot.bootstrapStatus === 'unknown_tenant';

  // INC-15b-3 (C, point 180): what nobody expected is said in words, and its
  // code goes to the log, never to the screen.
  const unexpected = (failure: unknown): void => {
    logUnexpected('sign in', failure);
    setError(t('access.unexpectedError'));
  };

  const submit = async (event: { preventDefault(): void }): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const label = deviceLabel.trim();
      await session.login(
        email.trim(),
        password,
        label === '' ? proposeDeviceLabel(navigator.userAgent, t) : label,
      );
      engine.trigger('manual');
    } catch (failure) {
      if (isNetworkError(failure)) setError(t('access.networkError'));
      else if (isApiError(failure)) {
        if (failure.code === CODES.invalidCredentials) setError(t('access.invalidCredentials'));
        else if (failure.code === CODES.rateLimited) {
          setError(
            t('access.rateLimited', { seconds: Math.ceil((failure.retryAfterMs ?? 0) / 1000) }),
          );
        } else if (failure.code === CODES.organizationSuspended) {
          setError(t('access.organizationSuspended'));
        } else unexpected(failure);
      } else unexpected(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="access"
      data-testid="access-screen"
      data-state={unknownTenant ? 'neutral' : 'branded'}
    >
      <BrandLockup
        name={organization !== null ? organization.name : t('access.neutralTitle')}
        logoUrl={brand.logoUrl}
        markUrl={brand.markUrl}
      />
      {unknownTenant ? (
        <div className="alert warn" role="alert" data-testid="unknown-tenant">
          {t('access.unknownTenant')}
        </div>
      ) : (
        <form
          className="card stack"
          onSubmit={(event) => void submit(event)}
          data-testid="access-form"
        >
          <h2 style={{ margin: 0 }}>{t('access.title')}</h2>
          {back?.expired === true ? (
            <div className="alert warn" role="status" data-testid="session-expired">
              {t('office.states.sessionExpired')}
            </div>
          ) : null}
          {pending > 0 ? (
            <div className="alert info" data-testid="pending-kept">
              {t('access.pendingKept', { count: pending })}
            </div>
          ) : null}
          <div className="field">
            <label htmlFor="email">{t('access.email')}</label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              required
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="password">{t('access.password')}</label>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              required
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="device-label">{t('access.deviceLabel')}</label>
            <input
              id="device-label"
              className="input"
              type="text"
              value={deviceLabel}
              aria-describedby="device-label-hint"
              onChange={(event) => {
                setDeviceLabelInput(event.target.value);
              }}
            />
            <span className="muted" id="device-label-hint" data-testid="device-label-hint">
              {t('access.deviceLabelHint')}
            </span>
          </div>
          {error !== null ? (
            <div className="alert error" role="alert" data-testid="access-error">
              {error}
            </div>
          ) : null}
          {/* INC-12a (A.4): the one action with the accent, its text by contrast. */}
          <button type="submit" className="btn accent block" disabled={busy}>
            {busy ? t('access.submitting') : t('access.submit')}
          </button>
          <Link className="btn secondary block" to="/recuperar" data-testid="access-forgot">
            {t('access.forgot')}
          </Link>
        </form>
      )}
    </div>
  );
}
